import { IMAGES, MAJOR_CLIENTS } from "../../data/site";

export const About = () => {
    return (
        <section
            id="about"
            data-testid="about-section"
            className="py-24 md:py-32 bg-bone relative overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-start">
                    {/* Left: image stack */}
                    <div className="lg:col-span-5 reveal hidden md:block">
                        <div className="relative">
                            <img
                                src={IMAGES.attic}
                                alt="HAZ crew at work in an attic"
                                loading="lazy"
                                className="w-full aspect-[4/5] object-cover"
                            />
                            <img
                                src={IMAGES.crawlSpace}
                                alt="Crawl space encapsulation finished"
                                loading="lazy"
                                className="absolute -bottom-12 -right-10 w-2/3 aspect-square object-cover border-8 border-bone shadow-2xl hidden lg:block"
                            />
                            {/* Stat overlay */}
                            <div className="absolute top-6 left-6 bg-bone p-5 max-w-[200px]">
                                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-moss">
                                    Established
                                </div>
                                <div className="font-heading text-4xl font-extrabold text-ink leading-none mt-1">
                                    HAZ Insulation
                                </div>
                                <div className="text-xs text-muted_ink mt-2">
                                    One crew, one standard.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right: story */}
                    <div className="lg:col-span-7 reveal">
                        <div className="text-xs tracking-[0.22em] uppercase font-bold text-moss mb-5">
                            <span className="h-px w-10 bg-moss inline-block align-middle mr-3" />
                            About the Company
                        </div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                            Built by installers.
                            <br />
                            <span className="text-copper">Run by a homeowner&apos;s advocate.</span>
                        </h2>

                        <div className="mt-8 space-y-5 text-ink/85 leading-relaxed text-base md:text-lg">
                            <p>
                                <strong className="text-ink">HAZ Insulation</strong> brings{" "}
                                <strong className="text-ink">50 years of combined experience</strong>{" "}
                                serving the Chicagoland area. We&apos;ve been insulating homes and
                                buildings across the region for the past{" "}
                                <strong className="text-ink">25 years</strong>.
                            </p>
                            <p>
                                <strong className="text-ink">James</strong>, one of the founders,
                                helped build Green Attic from a single truck into a fleet of 13
                                before his departure. His latest venture with{" "}
                                <strong className="text-ink">Titanium Insulation</strong> has
                                earned over <strong className="text-ink">50 five-star reviews</strong>{" "}
                                in less than 18 months.
                            </p>
                            <p>
                                The company has retrofitted more than{" "}
                                <strong className="text-ink">500 homes</strong>, along with
                                countless commercial buildings and new construction projects —
                                from single-family bungalows in Tinley Park to full-envelope
                                builds across the south suburbs and into NW Indiana. As an
                                approved <strong className="text-ink">NICOR &amp; ComEd</strong>{" "}
                                rebate contractor, we file the paperwork for you — most homeowners
                                qualify for{" "}
                                <strong className="text-copper">up to $1,800 in rebates</strong>.
                            </p>
                        </div>

                        {/* Major clients */}
                        <div className="mt-10 bg-ink text-bone p-7 md:p-8">
                            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-ember mb-3">
                                Trusted by
                            </div>
                            <div className="font-heading text-lg md:text-xl font-semibold leading-snug mb-5">
                                Major commercial &amp; builder clients include:
                            </div>
                            <ul
                                data-testid="major-clients-list"
                                className="grid grid-cols-2 gap-x-6 gap-y-3"
                            >
                                {MAJOR_CLIENTS.map((c) => (
                                    <li
                                        key={c}
                                        data-testid={`client-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                        className="font-heading font-bold text-base md:text-lg flex items-center gap-3"
                                    >
                                        <span className="h-1.5 w-1.5 bg-ember rounded-full shrink-0" />
                                        {c}
                                    </li>
                                ))}
                            </ul>
                            <div className="text-xs text-bone/60 mt-5">
                                — among other large-scale partners across Chicagoland.
                            </div>
                        </div>

                        {/* Pill stats row */}
                        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-px bg-line border border-line">
                            {[
                                { value: "25", label: "Years in Chicagoland" },
                                { value: "500+", label: "Homes retrofitted" },
                                { value: "50+", label: "5-star reviews" },
                                { value: "12", label: "Services offered" },
                            ].map((s) => (
                                <div
                                    key={s.label}
                                    data-testid={`about-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="bg-bone p-5 text-center"
                                >
                                    <div className="font-heading text-3xl font-extrabold text-copper">
                                        {s.value}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted_ink mt-1">
                                        {s.label}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <a
                                href="#quote"
                                data-testid="about-cta-quote"
                                className="btn-lift inline-flex items-center justify-center bg-copper text-bone font-semibold py-4 px-8 hover:bg-copper-hover"
                            >
                                Get a Free Quote
                            </a>
                            <a
                                href="#why"
                                data-testid="about-cta-why"
                                className="btn-lift inline-flex items-center justify-center border-2 border-ink text-ink font-semibold py-4 px-8 hover:bg-ink hover:text-bone"
                            >
                                Why Choose HAZ
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
